/**
 * Lead-time entry (DNKADM-0389 U4): a number plus a **Minutes / Hours / Days** unit
 * selector, shared by the tenant settings card and the service dialog so the two
 * controls behave identically. Storage and the wire stay minutes (`leadTimeMinutes`);
 * the caller converts with `parseLeadTimeEntry` on save and seeds with `splitLeadTime`
 * on load (the largest unit that divides the stored value evenly). The component owns
 * no conversion — it is the two controls and the helper/error line under them.
 *
 * Accessible names: the amount field is `amountAriaLabel`; the unit select is
 * `${amountAriaLabel} unit`. No placeholder or default number is ever rendered.
 */
import { useId, type ReactNode } from 'react';
import { FormControl, FormHelperText, MenuItem, Select, Stack, TextField } from '@mui/material';
import { LEAD_TIME_UNITS, leadTimeUnitTitle, type LeadTimeUnit } from '@/domain/leadTime';

export interface LeadTimeInputProps {
  /** The typed amount, as text (so an in-progress entry is never clobbered). */
  amount: string;
  unit: LeadTimeUnit;
  onAmountChange: (amount: string) => void;
  onUnitChange: (unit: LeadTimeUnit) => void;
  disabled?: boolean;
  error?: boolean;
  /** The line under both controls — the inline error when `error`, else guidance. */
  helperText?: ReactNode;
  /** A visible label for the amount field (the dialog); omit for the card's `hiddenLabel` form. */
  label?: string;
  /** The amount field's accessible name; the unit select is `${amountAriaLabel} unit`. */
  amountAriaLabel: string;
  id?: string;
  size?: 'small' | 'medium';
  /** Extra styling for the amount input (e.g. the card's mono face). */
  amountInputSx?: object;
  /** Width of the amount field. */
  amountWidth?: number | string;
}

export function LeadTimeInput({
  amount,
  unit,
  onAmountChange,
  onUnitChange,
  disabled = false,
  error = false,
  helperText,
  label,
  amountAriaLabel,
  id,
  size = 'medium',
  amountInputSx,
  amountWidth = 110,
}: LeadTimeInputProps) {
  // The helper/error line describes BOTH controls (the TextField's own helperText used to
  // wire this for the one input; here it sits under the pair).
  const helperId = `${useId()}-lead-time-help`;
  const hasHelper = helperText !== undefined && helperText !== null && helperText !== '';
  const describedBy = hasHelper ? { 'aria-describedby': helperId } : {};
  return (
    <FormControl error={error} disabled={disabled} component="div">
      <Stack direction="row" sx={{ gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <TextField
          id={id}
          {...(label !== undefined ? { label } : { hiddenLabel: true })}
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
          disabled={disabled}
          error={error}
          size={size}
          sx={{ width: amountWidth }}
          slotProps={{
            ...(amountInputSx !== undefined ? { input: { sx: amountInputSx } } : {}),
            htmlInput: { inputMode: 'numeric', 'aria-label': amountAriaLabel, ...describedBy },
          }}
        />
        <Select
          value={unit}
          onChange={(e) => onUnitChange(e.target.value as LeadTimeUnit)}
          disabled={disabled}
          error={error}
          size={size}
          sx={{ minWidth: 120 }}
          slotProps={{ input: { 'aria-label': `${amountAriaLabel} unit`, ...describedBy } }}
        >
          {LEAD_TIME_UNITS.map((u) => (
            <MenuItem key={u} value={u}>
              {leadTimeUnitTitle(u)}
            </MenuItem>
          ))}
        </Select>
      </Stack>
      {hasHelper && <FormHelperText id={helperId}>{helperText}</FormHelperText>}
    </FormControl>
  );
}
